export type GoogleMapType = 'roadmap' | 'satellite';

export interface GoogleTileSessionRequest {
  mapType: GoogleMapType;
  language: string;
  region: string;
  layerTypes?: string[];
}

export interface GoogleTileSession {
  session: string;
  expiresAt: number;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const GOOGLE_TILE_API_BASE = 'https://tile.googleapis.com';
export const SESSION_REFRESH_MARGIN_MS = 60 * 60 * 1000;
const STORAGE_PREFIX = 'atlas.googleTiles.session:';

export function getGoogleMapsBrowserKey(): string | null {
  const key = (process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY ?? '').trim();
  return key ? key : null;
}

export function buildSessionRequest(mapType: GoogleMapType, appLanguage: string | undefined): GoogleTileSessionRequest {
  const isPortuguese = (appLanguage ?? '').toLowerCase().startsWith('pt');
  const request: GoogleTileSessionRequest = {
    mapType,
    language: isPortuguese ? 'pt-BR' : 'en-AU',
    region: isPortuguese ? 'BR' : 'AU',
  };
  if (mapType === 'satellite') {
    request.layerTypes = ['layerRoadmap'];
  }
  return request;
}

export function sessionCacheKey(request: GoogleTileSessionRequest): string {
  return [request.mapType, request.language, request.region, (request.layerTypes ?? []).join('+')].join('|');
}

export function buildTileUrlTemplate(session: string, apiKey: string): string {
  return `${GOOGLE_TILE_API_BASE}/v1/2dtiles/{z}/{x}/{y}?session=${encodeURIComponent(session)}&key=${encodeURIComponent(apiKey)}`;
}

export class GoogleTileSessionError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'GoogleTileSessionError';
  }
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function parseSession(raw: unknown): GoogleTileSession | null {
  if (!raw || typeof raw !== 'object') return null;
  const { session, expiresAt } = raw as Record<string, unknown>;
  if (typeof session !== 'string' || !session || typeof expiresAt !== 'number') return null;
  return { session, expiresAt };
}

export interface GoogleTileSessionManagerOptions {
  fetchImpl?: FetchLike;
  storage?: StorageLike | null;
  now?: () => number;
}

export class GoogleTileSessionManager {
  private readonly fetchImpl: FetchLike;
  private readonly storage: StorageLike | null;
  private readonly now: () => number;
  private readonly cache = new Map<string, GoogleTileSession>();
  private readonly inFlight = new Map<string, Promise<GoogleTileSession>>();

  constructor(private readonly apiKey: string, options: GoogleTileSessionManagerOptions = {}) {
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    this.now = options.now ?? Date.now;
  }

  isFresh(session: GoogleTileSession): boolean {
    return session.expiresAt - this.now() > SESSION_REFRESH_MARGIN_MS;
  }

  msUntilRefresh(session: GoogleTileSession): number {
    return Math.max(0, session.expiresAt - SESSION_REFRESH_MARGIN_MS - this.now());
  }

  async getSession(request: GoogleTileSessionRequest): Promise<GoogleTileSession> {
    const key = sessionCacheKey(request);
    const cached = this.cache.get(key) ?? this.readStored(key);
    if (cached && this.isFresh(cached)) {
      this.cache.set(key, cached);
      return cached;
    }
    const pending = this.inFlight.get(key);
    if (pending) return pending;

    const created = this.createSession(request)
      .then((session) => {
        this.cache.set(key, session);
        this.writeStored(key, session);
        return session;
      })
      .finally(() => {
        this.inFlight.delete(key);
      });
    this.inFlight.set(key, created);
    return created;
  }

  async refresh(request: GoogleTileSessionRequest): Promise<GoogleTileSession> {
    this.invalidate(request);
    return this.getSession(request);
  }

  invalidate(request: GoogleTileSessionRequest): void {
    const key = sessionCacheKey(request);
    this.cache.delete(key);
    try {
      this.storage?.removeItem(STORAGE_PREFIX + key);
    } catch {
      // storage unavailable
    }
  }

  private async createSession(request: GoogleTileSessionRequest): Promise<GoogleTileSession> {
    const response = await this.fetchImpl(
      `${GOOGLE_TILE_API_BASE}/v1/createSession?key=${encodeURIComponent(this.apiKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    );
    if (!response.ok) {
      throw new GoogleTileSessionError(`createSession failed with status ${response.status}`, response.status);
    }
    const body = (await response.json()) as { session?: unknown; expiry?: unknown };
    const expirySeconds = Number(body.expiry);
    if (typeof body.session !== 'string' || !body.session || !Number.isFinite(expirySeconds)) {
      throw new GoogleTileSessionError('createSession returned an invalid session');
    }
    return { session: body.session, expiresAt: expirySeconds * 1000 };
  }

  private readStored(key: string): GoogleTileSession | null {
    try {
      const raw = this.storage?.getItem(STORAGE_PREFIX + key);
      return raw ? parseSession(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  private writeStored(key: string, session: GoogleTileSession): void {
    try {
      this.storage?.setItem(STORAGE_PREFIX + key, JSON.stringify(session));
    } catch {
      // storage unavailable
    }
  }
}

const managers = new Map<string, GoogleTileSessionManager>();

export function getGoogleTileSessionManager(apiKey: string): GoogleTileSessionManager {
  let manager = managers.get(apiKey);
  if (!manager) {
    manager = new GoogleTileSessionManager(apiKey);
    managers.set(apiKey, manager);
  }
  return manager;
}
